<template>
  <div class="seo-dashboard-page" v-loading="loading">
    <div class="seo-dashboard-page__header">
      <div>
        <div class="seo-dashboard-page__title">SEO 数据看板</div>
        <div class="seo-dashboard-page__desc">
          关键词排名、站点承诺与同步健康概览
        </div>
      </div>
      <div class="seo-dashboard-page__actions">
        <el-radio-group
          v-model="filters.time_range"
          size="small"
          @change="handleRangeChange"
        >
          <el-radio-button
            v-for="item in timeRangeOptions"
            :key="item.value"
            :label="item.value"
          >
            {{ item.label }}
          </el-radio-button>
        </el-radio-group>
        <el-date-picker
          v-if="filters.time_range === 'custom'"
          v-model="filters.custom_time"
          type="daterange"
          value-format="yyyy-MM-dd"
          range-separator="至"
          start-placeholder="开始时间"
          end-placeholder="结束时间"
          size="small"
        ></el-date-picker>
        <el-select
          v-model="filters.site_id"
          clearable
          filterable
          size="small"
          placeholder="全部站点"
          class="seo-dashboard-page__site-select"
        >
          <el-option
            v-for="item in siteOptions"
            :key="item.id"
            :label="item.name"
            :value="item.id"
          ></el-option>
        </el-select>
        <el-select
          v-model="filters.device"
          size="small"
          class="seo-dashboard-page__device-select"
        >
          <el-option label="桌面" value="desktop"></el-option>
          <el-option label="移动" value="mobile"></el-option>
          <el-option label="桌面+移动" value="all"></el-option>
        </el-select>
        <el-button size="small" icon="el-icon-refresh" @click="getSummary">
          刷新
        </el-button>
      </div>
    </div>

    <div class="seo-dashboard-page__freshness">
      数据口径：{{ currentRangeLabel }} / {{ currentSiteLabel }} /
      {{ currentDeviceLabel }}。页面读取本地排名快照，不实时请求 DataForSEO。
    </div>

    <div class="seo-dashboard-page__stats">
      <div
        v-for="card in metricCards"
        :key="card.key"
        class="seo-dashboard-page__stat-card"
      >
        <div class="seo-dashboard-page__stat-glow"></div>
        <div
          :class="[
            'seo-dashboard-page__stat-icon',
            `seo-dashboard-page__stat-icon--${card.tone}`,
          ]"
        >
          <i :class="card.icon"></i>
        </div>
        <div class="seo-dashboard-page__stat-main">
          <div class="seo-dashboard-page__stat-label">{{ card.label }}</div>
          <div class="seo-dashboard-page__stat-value">{{ card.value }}</div>
          <div class="seo-dashboard-page__stat-subline">
            <span class="seo-dashboard-page__stat-extra">{{ card.extra }}</span>
            <span
              :class="[
                'seo-dashboard-page__trend-badge',
                `seo-dashboard-page__trend-badge--${card.trend.direction}`,
              ]"
            >
              <i :class="card.trend.icon"></i>
              <span>{{ card.trend.text }}</span>
            </span>
          </div>
        </div>
      </div>
    </div>

    <div class="seo-dashboard-page__dashboard-grid">
      <div class="seo-dashboard-page__panel seo-dashboard-page__trend-panel">
        <div class="seo-dashboard-page__panel-head">
          <div>
            <div class="seo-dashboard-page__panel-title">最近快照趋势</div>
            <div class="seo-dashboard-page__panel-note">
              按检查日期聚合最近排名快照
            </div>
          </div>
          <span class="seo-dashboard-page__panel-badge">
            {{ snapshotScopeLabel }}
          </span>
        </div>
        <div ref="trendChart" class="seo-dashboard-page__chart"></div>
      </div>

      <div class="seo-dashboard-page__panel">
        <div class="seo-dashboard-page__panel-head">
          <div>
            <div class="seo-dashboard-page__panel-title">关键词类型分布</div>
            <div class="seo-dashboard-page__panel-note">
              按关键词业务类型统计
            </div>
          </div>
        </div>
        <div ref="typeChart" class="seo-dashboard-page__chart"></div>
      </div>
    </div>

    <div
      class="seo-dashboard-page__dashboard-grid seo-dashboard-page__dashboard-grid--middle"
    >
      <div class="seo-dashboard-page__panel">
        <div class="seo-dashboard-page__panel-head">
          <div>
            <div class="seo-dashboard-page__panel-title">站点达成率排行</div>
            <div class="seo-dashboard-page__panel-note">
              按承诺关键词达标数计算
            </div>
          </div>
        </div>
        <div ref="siteRankChart" class="seo-dashboard-page__chart"></div>
      </div>

      <div class="seo-dashboard-page__panel">
        <div class="seo-dashboard-page__panel-head">
          <div>
            <div class="seo-dashboard-page__panel-title">需要关注</div>
            <div class="seo-dashboard-page__panel-note">
              基于当前本地数据自动识别
            </div>
          </div>
        </div>
        <div class="seo-dashboard-page__attention-list">
          <div
            v-for="item in attentionItems"
            :key="item.title"
            :class="[
              'seo-dashboard-page__attention-item',
              `seo-dashboard-page__attention-item--${item.level}`,
            ]"
          >
            <div class="seo-dashboard-page__attention-icon">
              <i :class="item.icon"></i>
            </div>
            <div>
              <div class="seo-dashboard-page__attention-title">
                {{ item.title }}
              </div>
              <div class="seo-dashboard-page__attention-desc">
                {{ item.desc }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="seo-dashboard-page__panel">
      <div class="seo-dashboard-page__panel-head">
        <div>
          <div class="seo-dashboard-page__panel-title">站点关键词概览</div>
          <div class="seo-dashboard-page__panel-note">
            当前站点范围内的关键词覆盖、排名命中和承诺达成情况
          </div>
        </div>
      </div>
      <div class="data-table-scroll" style="--table-min-width: 1190px">
        <el-table
          :data="filteredSiteRankings"
          class="seo-dashboard-page__table"
          empty-text="暂无站点关键词数据"
        >
          <el-table-column
            prop="site_name"
            label="站点"
            min-width="150"
            show-overflow-tooltip
          ></el-table-column>
          <el-table-column
            prop="site_url"
            label="站点URL"
            min-width="220"
            show-overflow-tooltip
          ></el-table-column>
          <el-table-column
            prop="committed_keyword_count"
            label="承诺数"
            width="90"
          ></el-table-column>
          <el-table-column
            prop="keyword_count"
            label="关键词数"
            width="100"
          ></el-table-column>
          <el-table-column
            prop="rank_check_count"
            label="参与排名"
            width="100"
          ></el-table-column>
          <el-table-column prop="found_count" label="已找到" width="90">
            <template v-slot="scope">
              <span class="seo-dashboard-page__number-success">
                {{ scope.row.found_count || 0 }}
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="qualified_count" label="达标数" width="90">
            <template v-slot="scope">
              <span class="seo-dashboard-page__number-primary">
                {{ scope.row.qualified_count || 0 }}
              </span>
            </template>
          </el-table-column>
          <el-table-column label="承诺类型" width="100">
            <template v-slot="scope">
              {{ committedRankLimitText(scope.row.committed_rank_limit) }}
            </template>
          </el-table-column>
          <el-table-column prop="not_found_count" label="未找到" width="90">
            <template v-slot="scope">
              <span class="seo-dashboard-page__number-warning">
                {{ scope.row.not_found_count || 0 }}
              </span>
            </template>
          </el-table-column>
          <el-table-column label="达成率" width="160">
            <template v-slot="scope">
              <div class="seo-dashboard-page__rate-cell">
                <span>{{ scope.row.achievement_rate || "0.00%" }}</span>
                <el-progress
                  :percentage="progressRate(scope.row.achievement_rate)"
                  :show-text="false"
                  :stroke-width="6"
                  :color="rateColor(scope.row.achievement_rate)"
                ></el-progress>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>

    <div class="seo-dashboard-page__panel">
      <div class="seo-dashboard-page__panel-head">
        <div>
          <div class="seo-dashboard-page__panel-title">最近排名快照</div>
          <div class="seo-dashboard-page__panel-note">
            展示最近检查结果，便于快速定位排名波动和未找到关键词
          </div>
        </div>
      </div>
      <div class="data-table-scroll" style="--table-min-width: 740px">
        <el-table
          :data="filteredRecentSnapshots"
          class="seo-dashboard-page__table"
          empty-text="暂无排名快照"
        >
          <el-table-column
            prop="site_name"
            label="站点"
            width="150"
            show-overflow-tooltip
          ></el-table-column>
          <el-table-column label="关键词" min-width="240">
            <template v-slot="scope">
              <div class="seo-dashboard-page__keyword-cell">
                {{ scope.row.keyword || "-" }}
              </div>
            </template>
          </el-table-column>
          <el-table-column label="状态" width="110">
            <template v-slot="scope">
              <el-tag
                size="small"
                :type="rankStatusTagType(scope.row.rank_status)"
              >
                {{ rankStatusText(scope.row.rank_status, scope.row) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="排名" width="90">
            <template v-slot="scope">
              <strong
                v-if="scope.row.rank_position !== null"
                :class="rankNumberClass(scope.row.rank_position)"
              >
                {{ scope.row.rank_position }}
              </strong>
              <span v-else>-</span>
            </template>
          </el-table-column>
          <el-table-column label="检查时间" width="150">
            <template v-slot="scope">
              <div
                v-if="scope.row.checked_at"
                class="seo-dashboard-page__datetime"
              >
                <span>{{
                  formatDate(scope.row.checked_at, "yyyy-mm-dd")
                }}</span>
                <span>{{ formatDate(scope.row.checked_at, "hh:MM:ss") }}</span>
              </div>
              <span v-else>-</span>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from "echarts/core";
import {
  GraphicComponent,
  GridComponent,
  LegendComponent,
  TooltipComponent,
} from "echarts/components";
import { BarChart, LineChart, PieChart } from "echarts/charts";
import { CanvasRenderer } from "echarts/renderers";
import { apiSeoDashboardSummary } from "@/api/seo";
import { formatDate } from "@/utils/util";

echarts.use([
  GraphicComponent,
  GridComponent,
  LegendComponent,
  TooltipComponent,
  BarChart,
  LineChart,
  PieChart,
  CanvasRenderer,
]);

const timeRangeOptions = [
  { label: "今日", value: "today" },
  { label: "近7天", value: "7d" },
  { label: "近30天", value: "30d" },
  { label: "近半年", value: "half_year" },
  { label: "近一年", value: "1y" },
  { label: "自定义", value: "custom" },
];

export default {
  data() {
    return {
      loading: false,
      charts: {},
      timeRangeOptions,
      filters: {
        time_range: "30d",
        custom_time: [],
        site_id: "",
        device: "desktop",
      },
      summary: {
        overview: {},
        counts: {},
        keyword_types: {},
        rank_status: {},
        site_rankings: [],
        recent_snapshots: [],
      },
    };
  },
  computed: {
    overview() {
      return this.summary.overview || {};
    },
    counts() {
      return this.summary.counts || {};
    },
    keywordTypes() {
      return this.summary.keyword_types || {};
    },
    rankStatus() {
      return this.summary.rank_status || {};
    },
    siteRankings() {
      return this.summary.site_rankings || [];
    },
    recentSnapshots() {
      return this.summary.recent_snapshots || [];
    },
    siteOptions() {
      return this.siteRankings.map((item) => ({
        id: item.site_id,
        name: item.site_name || `站点 ${item.site_id}`,
      }));
    },
    currentRangeLabel() {
      const target = this.timeRangeOptions.find(
        (item) => item.value === this.filters.time_range
      );
      return target ? target.label : "近30天";
    },
    currentSiteLabel() {
      const target = this.siteOptions.find(
        (item) => item.id === this.filters.site_id
      );
      return target ? target.name : "全部站点";
    },
    currentDeviceLabel() {
      return { all: "桌面+移动", desktop: "桌面", mobile: "移动" }[
        this.filters.device
      ];
    },
    filteredSiteRankings() {
      if (!this.filters.site_id) return this.siteRankings;
      return this.siteRankings.filter(
        (item) => item.site_id === this.filters.site_id
      );
    },
    filteredRecentSnapshots() {
      if (!this.filters.site_id) return this.recentSnapshots;
      const site = this.siteOptions.find(
        (item) => item.id === this.filters.site_id
      );
      if (!site) return this.recentSnapshots;
      return this.recentSnapshots.filter(
        (item) => item.site_name === site.name
      );
    },
    snapshotScopeLabel() {
      const count = this.filteredRecentSnapshots.length;
      return count ? `最近 ${count} 条` : "暂无快照";
    },
    averageAchievementRate() {
      const rows = this.filteredSiteRankings;
      if (!rows.length) return 0;
      const total = rows.reduce(
        (sum, item) => sum + this.rateToNumber(item.achievement_rate),
        0
      );
      return total / rows.length;
    },
    scopedMetrics() {
      const rows = this.filteredSiteRankings;
      const committedSiteCount = rows.filter((item) => {
        const committed = Number(item.committed_keyword_count || 0);
        const qualified = Number(item.qualified_count || 0);
        return committed > 0 && qualified >= committed;
      }).length;
      return {
        site_count: rows.length || Number(this.overview.site_count || 0),
        keyword_count: rows.reduce(
          (sum, item) => sum + Number(item.keyword_count || 0),
          0
        ),
        rank_check_count: rows.reduce(
          (sum, item) => sum + Number(item.rank_check_count || 0),
          0
        ),
        found_count: rows.reduce(
          (sum, item) => sum + Number(item.found_count || 0),
          0
        ),
        not_found_count: rows.reduce(
          (sum, item) => sum + Number(item.not_found_count || 0),
          0
        ),
        qualified_site_count: committedSiteCount,
      };
    },
    foundRate() {
      const found = Number(this.scopedMetrics.found_count || 0);
      const total = Number(this.scopedMetrics.rank_check_count || 0);
      return total ? (found / total) * 100 : 0;
    },
    notFoundRate() {
      const notFound = Number(this.scopedMetrics.not_found_count || 0);
      const total = Number(this.scopedMetrics.rank_check_count || 0);
      return total ? (notFound / total) * 100 : 0;
    },
    metricCards() {
      return [
        {
          key: "sites",
          label: "站点总数",
          value: this.numberValue(this.scopedMetrics.site_count),
          extra: "已配置 SEO 站点",
          trend: this.trendMeta("flat", "暂无环比"),
          icon: "el-icon-monitor",
          tone: "blue",
        },
        {
          key: "keywords",
          label: "关键词总数",
          value: this.numberValue(this.scopedMetrics.keyword_count),
          extra: "全部关键词",
          trend: this.trendMeta("flat", "暂无环比"),
          icon: "el-icon-collection-tag",
          tone: "green",
        },
        {
          key: "rank_check",
          label: "参与排名",
          value: this.numberValue(this.scopedMetrics.rank_check_count),
          extra: "启用排名查询",
          trend: this.trendMeta(
            this.foundRate >= 60 ? "up" : this.foundRate > 0 ? "flat" : "down",
            `找到率 ${this.formatRate(this.foundRate)}`
          ),
          icon: "el-icon-data-line",
          tone: "purple",
        },
        {
          key: "qualified_sites",
          label: "达标站点",
          value: this.numberValue(this.scopedMetrics.qualified_site_count),
          extra: "完成承诺数目标",
          trend: this.trendMeta(
            this.averageAchievementRate >= 60
              ? "up"
              : this.averageAchievementRate > 0
              ? "flat"
              : "down",
            `均值 ${this.formatRate(this.averageAchievementRate)}`
          ),
          icon: "el-icon-medal",
          tone: "orange",
        },
        {
          key: "found",
          label: "已找到排名",
          value: this.numberValue(this.scopedMetrics.found_count),
          extra: `找到率 ${this.formatRate(this.foundRate)}`,
          trend: this.trendMeta(
            this.foundRate >= 60 ? "up" : this.foundRate > 0 ? "flat" : "down",
            `占比 ${this.formatRate(this.foundRate)}`
          ),
          icon: "el-icon-check",
          tone: "green",
        },
        {
          key: "not_found",
          label: "未找到排名",
          value: this.numberValue(this.scopedMetrics.not_found_count),
          extra: `未找到占比 ${this.formatRate(this.notFoundRate)}`,
          trend: this.trendMeta(
            this.notFoundRate > 40
              ? "down"
              : this.notFoundRate > 0
              ? "flat"
              : "up",
            `占比 ${this.formatRate(this.notFoundRate)}`
          ),
          icon: "el-icon-warning-outline",
          tone: "red",
        },
        {
          key: "achievement",
          label: "平均达成率",
          value: this.formatRate(this.averageAchievementRate),
          extra: "按站点达成率均值",
          trend: this.trendMeta(
            this.averageAchievementRate >= 60
              ? "up"
              : this.averageAchievementRate > 0
              ? "flat"
              : "down",
            `当前 ${this.formatRate(this.averageAchievementRate)}`
          ),
          icon: "el-icon-pie-chart",
          tone: "blue",
        },
        {
          key: "today_tasks",
          label: "今日任务",
          value: this.numberValue(this.counts.today_rank_tasks),
          extra: "本地任务记录",
          trend: this.trendMeta(
            Number(this.counts.today_rank_tasks || 0) > 0 ? "up" : "flat",
            Number(this.counts.today_rank_tasks || 0) > 0
              ? "今日有任务"
              : "今日无任务"
          ),
          icon: "el-icon-time",
          tone: "gray",
        },
      ];
    },
    attentionItems() {
      const rows = this.filteredSiteRankings;
      const zeroRows = rows.filter(
        (item) => this.rateToNumber(item.achievement_rate) === 0
      );
      const mostNotFound = rows
        .slice()
        .sort(
          (a, b) =>
            Number(b.not_found_count || 0) - Number(a.not_found_count || 0)
        )[0];
      const latestSnapshot = this.filteredRecentSnapshots[0];
      const items = [];
      if (zeroRows.length) {
        items.push({
          level: "danger",
          icon: "el-icon-warning-outline",
          title: `${zeroRows[0].site_name || "站点"} 达成率为 0.00%`,
          desc: `${zeroRows.length} 个站点当前没有达成承诺关键词目标`,
        });
      } else {
        items.push({
          level: "success",
          icon: "el-icon-check",
          title: "暂无 0 达成率站点",
          desc: "当前站点承诺达成情况稳定",
        });
      }
      if (mostNotFound && Number(mostNotFound.not_found_count || 0) > 0) {
        items.push({
          level: "warning",
          icon: "el-icon-search",
          title: `${mostNotFound.site_name || "站点"} 未找到关键词较多`,
          desc: `${Number(
            mostNotFound.not_found_count || 0
          )} 个关键词在当前深度内未找到排名`,
        });
      }
      items.push({
        level: latestSnapshot ? "info" : "warning",
        icon: "el-icon-refresh",
        title: latestSnapshot ? "最近同步快照已生成" : "暂无最近排名快照",
        desc: latestSnapshot
          ? `最近检查：${this.formatDate(
              latestSnapshot.checked_at,
              "yyyy-mm-dd hh:MM:ss"
            )}`
          : "请确认自动执行或采集任务是否正常",
      });
      return items;
    },
    trendRows() {
      const map = {};
      this.filteredRecentSnapshots.forEach((item) => {
        const label = item.checked_at
          ? this.formatDate(item.checked_at, "mm-dd")
          : "-";
        if (!map[label]) {
          map[label] = { label, found: 0, not_found: 0, top10: 0 };
        }
        if (item.rank_status === "found") {
          map[label].found += 1;
          if (
            Number(item.rank_position || 0) > 0 &&
            Number(item.rank_position) <= 10
          ) {
            map[label].top10 += 1;
          }
        } else {
          map[label].not_found += 1;
        }
      });
      return Object.keys(map).map((key) => map[key]);
    },
  },
  created() {
    this.getSummary();
  },
  mounted() {
    window.addEventListener("resize", this.resizeCharts);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.resizeCharts);
    Object.keys(this.charts).forEach((key) => {
      this.charts[key].dispose();
    });
    this.charts = {};
  },
  watch: {
    filteredSiteRankings() {
      this.$nextTick(this.renderCharts);
    },
    filteredRecentSnapshots() {
      this.$nextTick(this.renderCharts);
    },
  },
  methods: {
    formatDate,
    getSummary() {
      this.loading = true;
      apiSeoDashboardSummary()
        .then((resp) => {
          this.summary = resp || {};
          this.$nextTick(this.renderCharts);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    handleRangeChange() {
      if (this.filters.time_range !== "custom") {
        this.filters.custom_time = [];
      }
    },
    chart(ref) {
      if (!this.$refs[ref]) return null;
      if (!this.charts[ref]) {
        this.charts[ref] = echarts.init(this.$refs[ref]);
      }
      return this.charts[ref];
    },
    renderCharts() {
      this.renderTrendChart();
      this.renderTypeChart();
      this.renderSiteRankChart();
      this.resizeCharts();
    },
    renderTrendChart() {
      const chart = this.chart("trendChart");
      if (!chart) return;
      const rows = this.trendRows;
      chart.setOption({
        color: ["#2f7cf6", "#18c48f", "#ff9f1a"],
        tooltip: {
          trigger: "axis",
          confine: true,
          backgroundColor: "rgba(24, 38, 61, 0.92)",
          borderWidth: 0,
          textStyle: { color: "#fff" },
        },
        legend: {
          top: 0,
          right: 0,
          itemWidth: 18,
          itemHeight: 8,
          textStyle: { color: "#607089", fontSize: 12 },
        },
        grid: { top: 42, right: 18, bottom: 30, left: 42 },
        xAxis: {
          type: "category",
          boundaryGap: false,
          data: rows.map((item) => item.label),
          axisTick: { show: false },
          axisLine: { lineStyle: { color: "#dfe6f0" } },
          axisLabel: { color: "#607089" },
        },
        yAxis: {
          type: "value",
          minInterval: 1,
          splitLine: { lineStyle: { color: "#edf1f7" } },
          axisLabel: { color: "#607089" },
        },
        series: [
          this.lineSeries(
            "已找到",
            rows.map((item) => item.found),
            true,
            "#2f7cf6"
          ),
          this.lineSeries(
            "Top10",
            rows.map((item) => item.top10),
            false,
            "#18c48f"
          ),
          this.lineSeries(
            "未找到",
            rows.map((item) => item.not_found),
            false,
            "#ff9f1a"
          ),
        ],
      });
    },
    renderTypeChart() {
      const chart = this.chart("typeChart");
      if (!chart) return;
      const rows = [
        { name: "主词", value: Number(this.keywordTypes.core || 0) },
        { name: "长尾词", value: Number(this.keywordTypes.long_tail || 0) },
        { name: "品牌词", value: Number(this.keywordTypes.brand || 0) },
        { name: "竞品词", value: Number(this.keywordTypes.competitor || 0) },
      ];
      const total = rows.reduce((sum, item) => sum + item.value, 0);
      chart.setOption({
        color: ["#2f7cf6", "#18c48f", "#ffb020", "#ff5a5f"],
        tooltip: { trigger: "item", confine: true },
        legend: {
          orient: "vertical",
          top: "middle",
          right: 16,
          itemWidth: 11,
          itemHeight: 11,
          icon: "circle",
          textStyle: { color: "#607089", fontSize: 12 },
        },
        graphic: [
          {
            type: "text",
            left: "31%",
            top: "42%",
            style: {
              text: this.numberValue(total),
              textAlign: "center",
              fill: "#18263d",
              fontSize: 26,
              fontWeight: 700,
            },
          },
          {
            type: "text",
            left: "31%",
            top: "53%",
            style: {
              text: "关键词",
              textAlign: "center",
              fill: "#8a97aa",
              fontSize: 13,
            },
          },
        ],
        series: [
          {
            name: "关键词类型",
            type: "pie",
            radius: ["52%", "74%"],
            center: ["32%", "50%"],
            minAngle: 4,
            label: { show: false },
            labelLine: { show: false },
            itemStyle: {
              borderColor: "#fff",
              borderWidth: 5,
              borderRadius: 8,
              shadowBlur: 12,
              shadowColor: "rgba(34, 50, 74, 0.12)",
            },
            data: rows,
          },
        ],
      });
    },
    renderSiteRankChart() {
      const chart = this.chart("siteRankChart");
      if (!chart) return;
      const rows = this.filteredSiteRankings
        .slice()
        .sort(
          (a, b) =>
            this.rateToNumber(a.achievement_rate) -
            this.rateToNumber(b.achievement_rate)
        );
      chart.setOption({
        color: ["#2f7cf6"],
        tooltip: {
          trigger: "axis",
          axisPointer: { type: "shadow" },
          confine: true,
          backgroundColor: "rgba(24, 38, 61, 0.92)",
          borderWidth: 0,
          textStyle: { color: "#fff" },
          formatter: (params) => {
            const item = params && params[0];
            return item ? `${item.name}<br/>达成率：${item.value}%` : "";
          },
        },
        grid: { top: 10, right: 50, bottom: 22, left: 104 },
        xAxis: {
          type: "value",
          max: 100,
          splitLine: { lineStyle: { color: "#edf1f7" } },
          axisLabel: { color: "#607089", formatter: "{value}%" },
        },
        yAxis: {
          type: "category",
          data: rows.map((item) => this.shortText(item.site_name || "-", 12)),
          axisTick: { show: false },
          axisLine: { lineStyle: { color: "#dfe6f0" } },
          axisLabel: { color: "#607089" },
        },
        series: [
          {
            name: "达成率",
            type: "bar",
            barWidth: 15,
            data: rows.map((item) => this.rateToNumber(item.achievement_rate)),
            label: {
              show: true,
              position: "right",
              formatter: "{c}%",
              color: "#31415a",
              fontWeight: 600,
            },
            itemStyle: {
              borderRadius: [0, 8, 8, 0],
              color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
                { offset: 0, color: "#18c48f" },
                { offset: 1, color: "#2f7cf6" },
              ]),
              shadowBlur: 10,
              shadowColor: "rgba(47, 124, 246, 0.22)",
            },
          },
        ],
      });
    },
    lineSeries(name, data, area, color) {
      return {
        name,
        type: "line",
        smooth: true,
        symbol: "circle",
        symbolSize: 6,
        data: data.map((item) => Number(item || 0)),
        areaStyle: area
          ? {
              opacity: 1,
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: this.hexToRgba(color, 0.24) },
                { offset: 1, color: this.hexToRgba(color, 0.02) },
              ]),
            }
          : undefined,
        itemStyle: {
          color,
          borderColor: "#fff",
          borderWidth: 2,
        },
        lineStyle: {
          width: 3,
          color,
          shadowBlur: 8,
          shadowColor: this.hexToRgba(color, 0.28),
        },
      };
    },
    resizeCharts() {
      Object.keys(this.charts).forEach((key) => {
        this.charts[key].resize();
      });
    },
    numberValue(value, fallback = 0) {
      const target = value === undefined || value === null ? fallback : value;
      return Number(target || 0).toLocaleString();
    },
    formatRate(value) {
      const number = Number(value || 0);
      return `${number.toFixed(2)}%`;
    },
    trendMeta(direction, text) {
      const iconMap = {
        up: "el-icon-top",
        down: "el-icon-bottom",
        flat: "el-icon-minus",
      };
      return {
        direction,
        text,
        icon: iconMap[direction] || iconMap.flat,
      };
    },
    rateToNumber(value) {
      if (value === undefined || value === null || value === "") return 0;
      const number = Number(String(value).replace("%", ""));
      return Number.isNaN(number) ? 0 : Number(number.toFixed(2));
    },
    progressRate(value) {
      const number = this.rateToNumber(value);
      return Math.max(0, Math.min(100, number));
    },
    rateColor(value) {
      const number = this.rateToNumber(value);
      if (number >= 60) return "#18c48f";
      if (number >= 30) return "#2f7cf6";
      if (number > 0) return "#ffb020";
      return "#ff5a5f";
    },
    rankStatusText(status, row = {}) {
      if (status === "not_found") {
        return `前${row.depth || 50}未找到`;
      }
      return { found: "已找到" }[status] || "-";
    },
    rankStatusTagType(status) {
      return { found: "success", not_found: "info" }[status] || "info";
    },
    committedRankLimitText(value) {
      const limit = Number(value || 30);
      return limit === 10 ? "前10" : limit === 50 ? "前50" : "前30";
    },
    rankNumberClass(value) {
      const number = Number(value || 0);
      if (number > 0 && number <= 10)
        return "seo-dashboard-page__number-success";
      if (number > 10 && number <= 30)
        return "seo-dashboard-page__number-primary";
      return "seo-dashboard-page__number-warning";
    },
    hexToRgba(hex, opacity) {
      const value = String(hex || "#1677ff").replace("#", "");
      const full =
        value.length === 3
          ? value
              .split("")
              .map((item) => item + item)
              .join("")
          : value;
      const r = parseInt(full.slice(0, 2), 16);
      const g = parseInt(full.slice(2, 4), 16);
      const b = parseInt(full.slice(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    },
    shortText(text, length) {
      text = String(text || "");
      return text.length > length ? `${text.slice(0, length)}...` : text;
    },
  },
};
</script>

<style lang="scss" scoped>
.seo-dashboard-page {
  min-height: calc(100vh - 84px);
  padding: 16px;
  background: #f4f7fb;
  color: #18263d;

  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 12px;
  }

  &__title {
    color: #18263d;
    font-size: 22px;
    font-weight: 700;
    line-height: 30px;
  }

  &__desc {
    margin-top: 4px;
    color: #758399;
    font-size: 13px;
  }

  &__actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 8px;
  }

  &__site-select {
    width: 190px;
  }

  &__device-select {
    width: 116px;
  }

  &__freshness {
    margin-bottom: 14px;
    border: 1px solid #dfe8f6;
    border-radius: 8px;
    background: #fff;
    padding: 10px 14px;
    color: #607089;
    font-size: 13px;
    line-height: 20px;
  }

  &__stats {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 14px;
    margin-bottom: 14px;
  }

  &__stat-card {
    position: relative;
    display: flex;
    align-items: center;
    min-height: 92px;
    overflow: hidden;
    border: 1px solid #e6edf6;
    border-radius: 8px;
    background: #fff;
    padding: 15px 16px;
    box-shadow: 0 8px 22px rgba(34, 50, 74, 0.05);
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }

  &__stat-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 14px 28px rgba(34, 50, 74, 0.08);
  }

  &__stat-glow {
    position: absolute;
    right: -22px;
    top: -28px;
    width: 78px;
    height: 78px;
    border-radius: 50%;
    background: rgba(47, 124, 246, 0.08);
  }

  &__stat-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 40px;
    width: 40px;
    height: 40px;
    margin-right: 12px;
    border-radius: 8px;
    color: #fff;
    font-size: 19px;
    box-shadow: 0 10px 20px rgba(34, 50, 74, 0.16);
  }

  &__stat-icon--blue {
    background: linear-gradient(135deg, #2f7cf6, #56a5ff);
  }

  &__stat-icon--green {
    background: linear-gradient(135deg, #18b884, #1dd7b5);
  }

  &__stat-icon--purple {
    background: linear-gradient(135deg, #6c63ff, #9b8cff);
  }

  &__stat-icon--orange {
    background: linear-gradient(135deg, #ff9f1a, #ffcc4d);
  }

  &__stat-icon--red {
    background: linear-gradient(135deg, #ff4d57, #ff8b7b);
  }

  &__stat-icon--gray {
    background: linear-gradient(135deg, #607089, #9aa8ba);
  }

  &__stat-main {
    min-width: 0;
  }

  &__stat-label {
    color: #758399;
    font-size: 13px;
    line-height: 18px;
  }

  &__stat-value {
    margin-top: 4px;
    color: #18263d;
    font-size: 24px;
    font-weight: 700;
    line-height: 29px;
  }

  &__stat-subline {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 7px;
    margin-top: 5px;
    min-width: 0;
  }

  &__stat-extra {
    color: #8a97aa;
    font-size: 12px;
    line-height: 17px;
    white-space: nowrap;
  }

  &__trend-badge {
    display: inline-flex;
    align-items: center;
    max-width: 100%;
    border-radius: 999px;
    padding: 2px 8px;
    font-size: 12px;
    line-height: 17px;
    white-space: nowrap;
  }

  &__trend-badge i {
    margin-right: 4px;
    font-size: 12px;
  }

  &__trend-badge--up {
    background: #e9fbf5;
    color: #18a979;
  }

  &__trend-badge--down {
    background: #fff1f0;
    color: #e64a55;
  }

  &__trend-badge--flat {
    background: #eef5ff;
    color: #607089;
  }

  &__dashboard-grid {
    display: grid;
    grid-template-columns: minmax(0, 1.45fr) minmax(360px, 0.85fr);
    gap: 14px;
    margin-bottom: 14px;
  }

  &__dashboard-grid--middle {
    grid-template-columns: minmax(0, 1fr) minmax(360px, 0.8fr);
  }

  &__panel {
    border: 1px solid #e6edf6;
    border-radius: 8px;
    background: #fff;
    padding: 16px;
    box-shadow: 0 8px 22px rgba(34, 50, 74, 0.04);
  }

  &__panel + &__panel {
    margin-top: 14px;
  }

  &__dashboard-grid &__panel + &__panel {
    margin-top: 0;
  }

  &__panel-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
  }

  &__panel-title {
    color: #18263d;
    font-size: 16px;
    font-weight: 700;
    line-height: 22px;
  }

  &__panel-note {
    margin-top: 3px;
    color: #8a97aa;
    font-size: 12px;
    line-height: 18px;
  }

  &__panel-badge {
    flex: 0 0 auto;
    border-radius: 999px;
    background: #eef5ff;
    padding: 4px 10px;
    color: #2f7cf6;
    font-size: 12px;
  }

  &__chart {
    height: 260px;
  }

  &__trend-panel &__chart {
    height: 280px;
  }

  &__attention-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__attention-item {
    display: flex;
    align-items: flex-start;
    min-height: 72px;
    border: 1px solid #edf1f7;
    border-radius: 8px;
    padding: 13px 14px;
    background: #fbfdff;
  }

  &__attention-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 34px;
    width: 34px;
    height: 34px;
    margin-right: 12px;
    border-radius: 8px;
    font-size: 18px;
  }

  &__attention-item--danger &__attention-icon {
    background: #fff1f0;
    color: #ff4d57;
  }

  &__attention-item--warning &__attention-icon {
    background: #fff7e6;
    color: #ff9f1a;
  }

  &__attention-item--success &__attention-icon {
    background: #e9fbf5;
    color: #18b884;
  }

  &__attention-item--info &__attention-icon {
    background: #eef5ff;
    color: #2f7cf6;
  }

  &__attention-title {
    color: #26364d;
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
  }

  &__attention-desc {
    margin-top: 4px;
    color: #758399;
    font-size: 12px;
    line-height: 18px;
  }

  &__table {
    ::v-deep .el-table__header th {
      background: #f7f9fc;
      color: #607089;
      font-weight: 600;
    }

    ::v-deep .el-table__row:hover > td {
      background: #f8fbff;
    }
  }

  &__rate-cell {
    display: grid;
    grid-template-columns: 58px minmax(64px, 1fr);
    align-items: center;
    gap: 8px;
    color: #31415a;
    font-weight: 600;
  }

  &__keyword-cell {
    display: -webkit-box;
    overflow: hidden;
    line-height: 20px;
    white-space: normal;
    word-break: break-word;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  }

  &__datetime {
    display: flex;
    flex-direction: column;
    color: #607089;
    line-height: 20px;
  }

  &__number-success {
    color: #18b884;
    font-weight: 700;
  }

  &__number-primary {
    color: #2f7cf6;
    font-weight: 700;
  }

  &__number-warning {
    color: #ff9f1a;
    font-weight: 700;
  }
}

@media (max-width: 1280px) {
  .seo-dashboard-page {
    &__stats {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    &__dashboard-grid,
    &__dashboard-grid--middle {
      grid-template-columns: minmax(0, 1fr);
    }
  }
}

@media (max-width: 1400px) {
  .seo-dashboard-page {
    &__header {
      flex-direction: column;
    }

    &__actions {
      width: 100%;
      justify-content: flex-start;
    }
  }
}
</style>
